import React, { useContext, useEffect, useMemo, useState } from "react";
import { useUpdateKey } from "@repoflow/components/update-key";

// What the editor screen knows that no single form does: which element is
// selected, and how the preview beside it should behave.
//
// It is a context rather than props because the two ends are far apart in the
// tree — the crosshair that selects an element is a control inside a list
// widget inside a form, and the thing that reacts to it is the iframe in the
// other pane. Threading a callback down through the form's config would mean
// the config changes identity whenever the selection does, which rebuilds the
// widget tree underneath it (P139).
//
// Ported from the archived project's @repoflow/components/editor.

const SETTINGS_KEY = "repoflow.editor";

const DEFAULTS = { auto: true, highlight: true };

// Settings survive a reload; the SELECTION does not. Opening a page with an
// element already highlighted, from a session hours ago, would be confusing —
// and the index it names may no longer exist.
const readSettings = () => {
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    const saved = raw ? JSON.parse(raw) : null;

    return saved
      ? { auto: saved.auto !== false, highlight: saved.highlight !== false }
      : DEFAULTS;
  } catch (e) {
    // Private mode, a disabled store, or a value somebody hand-edited. None of
    // them is worth failing the screen over.
    return DEFAULTS;
  }
};

const Context = React.createContext(null);

export const useEditor = () => useContext(Context);

export const Provider = ({ children }) => {
  const [state, setState] = useState(() => ({ selected: null, ...readSettings() }));
  const { key, update, throtUpdate, debounceUpdate } = useUpdateKey();

  useEffect(() => {
    try {
      window.localStorage.setItem(
        SETTINGS_KEY,
        JSON.stringify({ auto: state.auto, highlight: state.highlight })
      );
    } catch (e) {
      // See readSettings.
    }
  }, [state.auto, state.highlight]);

  const context = useMemo(
    () => ({
      ...state,
      // Clicking the crosshair of the element already selected clears it, which
      // is what removes the dimmer from the preview. Same control, both ways.
      select: (raw) => {
        const index = raw === null || raw === undefined ? null : parseInt(raw, 10);

        setState((current) => ({
          ...current,
          selected: current.selected === index ? null : index,
        }));
      },
      // Unconditional, for the callers that must not toggle: the element route
      // selects on mount and deselects on unmount, and a toggle there would
      // clear a selection the route had just made.
      setSelected: (selected) => setState((current) => ({ ...current, selected })),
      setAuto: (auto) => setState((current) => ({ ...current, auto })),
      setHighlight: (highlight) => setState((current) => ({ ...current, highlight })),
      setSettings: (settings) => setState((current) => ({ ...current, ...settings })),
      key,
      update,
      throtUpdate,
      debounceUpdate,
    }),
    [state, key, update, throtUpdate, debounceUpdate]
  );

  return <Context.Provider value={context}>{children}</Context.Provider>;
};
